import styled from 'styled-components'

import { LocusPageDataContainer } from './LocusPageData'
import { GenePageControls } from './GenePageControls'
import { useRecoilState, useRecoilValue } from 'recoil'
import {
  analysisIdAtom,
  geneIdAtom,
  hideGeneOptsAtom,
  regionIdAtom,
  variantIdAtom,
} from '../sharedState'
import { ShowControlsButton } from '../UserInterface'
import { getDetailOptionsLabel, optionPanelContract } from '../browserUiContracts'
import { resolveGenomicContext } from './genomicContext'

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 2rem;
  color: ${(props) => props.theme.text};
  text-align: center;
`

const Container = styled.div<{ $embedded: boolean }>`
  display: flex;
  flex-direction: row;
  width: 100%;
  min-width: 0;
  height: ${({ $embedded }) => ($embedded ? 'auto' : '100%')};
  min-height: ${({ $embedded }) => ($embedded ? '0' : '100%')};
  max-width: 100%;
  overflow: ${({ $embedded }) => ($embedded ? 'visible' : 'hidden')};
  position: relative;

  & > :first-child {
    flex: 1 1 auto;
    min-width: 0;
  }

  @media (max-width: 600px) {
    flex-direction: column;
    overflow-y: ${({ $embedded }) => ($embedded ? 'visible' : 'auto')};

    & > :first-child {
      order: 2;
      flex: 1 1 auto;
      width: 100%;
      min-height: ${({ $embedded }) => ($embedded ? '0' : '320px')};
    }

    & > :not(:first-child) {
      order: 1;
    }
  }
`

export const LocusPageRoot: React.FC<{ embedded?: boolean }> = ({
  embedded = false,
}) => {
  const [hideGeneOptions, setHideGeneOptions] = useRecoilState(hideGeneOptsAtom)
  const analysisId = useRecoilValue(analysisIdAtom)
  const geneId = useRecoilValue(geneIdAtom)
  const regionId = useRecoilValue(regionIdAtom)
  const variantId = useRecoilValue(variantIdAtom)
  const context = resolveGenomicContext({ variantId, geneId, regionId })
  const optionsLabel = getDetailOptionsLabel({
    variantId,
    regionId: context.kind === 'locus' ? context.regionId : null,
  })

  if (!analysisId) {
    return <EmptyState>Choose a phenotype to see association details.</EmptyState>
  }

  if (!geneId && !regionId && !variantId) {
    return <EmptyState>Choose a gene, locus, or variant to see details.</EmptyState>
  }

  return (
    <Container $embedded={embedded} data-locus-layout={embedded ? 'embedded' : 'standalone'}>
      <LocusPageDataContainer embedded={embedded} context={context} />
      {!hideGeneOptions && (
        <GenePageControls embedded={embedded} contextKind={context.kind} />
      )}
      {hideGeneOptions && (
        <ShowControlsButton
          type="button"
          $right
          onClick={() => setHideGeneOptions(false)}
          title={`Show ${optionsLabel}`}
          aria-label={`Show ${optionsLabel}`}
          aria-expanded={false}
          aria-controls={optionPanelContract.detail.id}
        >
          {optionsLabel}
        </ShowControlsButton>
      )}
    </Container>
  )
}
